import React, { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import Checkbox from '@mui/material/Checkbox';

import './mobile-cart-line-style.scss';
import { CredentialContext, credentialType } from '@providers';
import { useSnackbar } from '@src/providers/SnackbarProvider';

// Components
import Button from '@src/componentsNew/Buttons/Button';
import Typography from '@src/componentsNew/Typography/Typography';
import ReduceFilledIcon from '@src/componentsNew/Icons/Icons/ReduceFilledIcon';
import AddFilledIcon from '@src/componentsNew/Icons/Icons/AddFilledIcon';
import DeleteIcon from '@src/componentsNew/Icons/Icons/DeleteIcon';
import WishlistIcon from '@src/componentsNew/Icons/Icons/WishlistIcon';

import { CartSubCartsLinesType } from '@src/utils/types';

import { getGraphqlIdFromDBId } from '@utils';
import { getPriceInfoFromVariant, formatPrice, formatWeight } from '@src/utils';
import { MAX_CART_LINE_QUANTITY } from '@src/utils/constants';
import { wishListUrl } from '@src/app/routes';

interface IProps {
  line: CartSubCartsLinesType;
  activeCart: 'maccaron' | 'mgs';
  loading: boolean;
  updateCartLine?: any;
  deleteCartLine?: any;
  cartLineToWishlist?: any;
}

const MobileCartLine = ({ line, activeCart, loading, updateCartLine, deleteCartLine, cartLineToWishlist }: IProps) => {
  const history = useHistory();
  const { showSnackbar } = useSnackbar();
  const { currentCart } = useContext<credentialType>(CredentialContext);
  const cartId = getGraphqlIdFromDBId(
    activeCart === 'maccaron' ? currentCart?.maccaronCart?.token : currentCart?.mgsCart?.token,
    'Cart',
  );

  const { originalPrice, finalPrice, discountPercent, isOnSale } = getPriceInfoFromVariant(line?.variant);

  const handleClickCartLine = (e, urlForGraphql: string) => {
    e.stopPropagation();

    if (loading) return;

    history.push(urlForGraphql);
  };

  const handleClickCartLineCheckbox = (e, line: CartSubCartsLinesType) => {
    e.stopPropagation();

    updateCartLine({
      variables: {
        cartId,
        input: [
          {
            lineId: line.id,
            quantity: line.quantity,
            selected: e.target.checked,
          },
        ],
      },
    });
  };

  const stopEventPropagationFunc = (e) => {
    e.stopPropagation();
  };

  const handleClickCartLineQuantityButton = (e, line: CartSubCartsLinesType, action: 'plus' | 'minus') => {
    e.stopPropagation();

    if (line.quantity <= 1 && action === 'minus') {
      return;
    }
    if (line.quantity >= MAX_CART_LINE_QUANTITY && action === 'plus') {
      return;
    }

    const newQuantity = action === 'plus' ? line.quantity + 1 : line.quantity - 1;

    if (line?.variant?.stockQuantity < newQuantity && action === 'plus') {
      return;
    }

    updateCartLine({
      variables: {
        cartId,
        input: [
          {
            lineId: line.id,
            quantity: newQuantity,
            selected: line.selected,
          },
        ],
      },
    });
  };

  const handleClickCartLineRemoveButton = (e, lineId: string, productName: string, moveWishlist: boolean) => {
    e.stopPropagation();
    const cartId = getGraphqlIdFromDBId(
      activeCart === 'maccaron' ? currentCart?.maccaronCart?.token : currentCart?.mgsCart?.token,
      'Cart',
    );

    if (moveWishlist) {
      cartLineToWishlist({
        variables: {
          input: {
            cartId,
            lineId,
          },
          productId: line?.variant?.product?.id,
        },
      }).then((data) => {
        if (data?.data?.wishlistAddProduct?.errors.length === 0) {
          showSnackbar({
            message: `${productName} moved to wishlist`,
            isShowBtn: true,
            btnContent: 'Wishlist',
            btnOnClick: () => history.push(wishListUrl),
          });
        }
      });
    } else {
      deleteCartLine({
        variables: {
          cartId,
          lineId,
        },
      }).then((data) => {
        if (data?.data?.cartLineDelete?.errors.length === 0) {
          showSnackbar({ message: `${productName} removed` });
        }
      });
    }
  };

  return (
    <div className="mobile-cart-line-wrapper">
      <div className="product-info-container">
        <div className="brand-product-name-wrapper">
          <Checkbox
            className={`cart-line-checkbox ${activeCart}`}
            size="small"
            disabled={loading}
            checked={line?.selected}
            onChange={(e) => handleClickCartLineCheckbox(e, line)}
          />

          <Typography cName="brand-name" variant="body3" component="span" style={{ color: '#212121' }}>
            {`[${line?.variant?.product?.brand}] ${line?.variant?.product?.name}`}
          </Typography>
        </div>

        <div
          className="cart-line-product-wrapper"
          onClick={(e) => handleClickCartLine(e, line?.variant.product.urlForGraphql)}
        >
          <img
            className="product-image"
            src={line?.variant?.product?.thumbnail?.url}
            alt={line?.variant?.product?.thumbnail?.alt}
          />

          <div className="product-description-wrapper">
            {(line?.variant?.name || !line?.variant?.product?.productType.isCodRestricted) && (
              <div className="varinat-name-wrapper">
                {line?.variant?.name && (
                  <Typography variant="caption" component="span" style={{ color: '#666666' }}>
                    {line?.variant?.name}
                  </Typography>
                )}
                {!line?.variant?.product?.productType.isCodRestricted ? (
                  <Typography cName="product-type-item" variant="caption" component="span">
                    COD
                  </Typography>
                ) : null}
              </div>
            )}

            {line?.variant?.product?.productType?.isMgs && (
              <Typography cName="mgs-product-weight" variant="caption" component="span" style={{ color: '#11141D99' }}>
                weight : {formatWeight(line?.variant?.displayWeight.value, line?.quantity)}
                {line?.variant?.displayWeight.unit}
              </Typography>
            )}

            <div className="price-count-wrapper">
              <div className="price-wrapper">
                <Typography variant="body2" component="span">
                  {formatPrice(finalPrice * line?.quantity)}
                </Typography>
                {isOnSale && (
                  <>
                    <Typography variant="body2" component="span" style={{ color: '#FF3399' }}>
                      {discountPercent}%
                    </Typography>
                    <Typography variant="strikethrough2" component="span" style={{ color: '#11141D61' }}>
                      {formatPrice(originalPrice * line?.quantity)}
                    </Typography>
                  </>
                )}
              </div>

              <div className="count-control-wrapper">
                <Button
                  cName="count-control"
                  size="medium"
                  disabled={loading}
                  onClick={(e) => handleClickCartLineQuantityButton(e, line, 'minus')}
                >
                  <ReduceFilledIcon strokeColor={line.quantity <= 1 ? '#9E9E9E' : '#212121'} />
                </Button>
                <Typography variant="body2" component="span" onClick={(e) => stopEventPropagationFunc(e)}>
                  {line?.quantity}
                </Typography>
                <Button
                  cName="count-control"
                  size="medium"
                  disabled={loading}
                  onClick={(e) => handleClickCartLineQuantityButton(e, line, 'plus')}
                >
                  <AddFilledIcon
                    strokeColor={
                      line.quantity >= MAX_CART_LINE_QUANTITY || line?.variant?.stockQuantity <= line.quantity
                        ? '#9E9E9E'
                        : '#212121'
                    }
                  />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="delete-button-wrapper">
        <Button
          cName="delete-btn"
          size="small"
          disabled={loading}
          startIcon={<DeleteIcon width="18" height="18" fillColor="#2E394642" />}
          onClick={(e) => handleClickCartLineRemoveButton(e, line.id, line?.variant?.product?.name, false)}
        >
          Remove
        </Button>
        <div className="vertical-divider" />
        <Button
          cName="move-to-wishlist-btn"
          size="small"
          disabled={loading}
          startIcon={<WishlistIcon width="18" height="18" fillColor="#2E394642" />}
          onClick={(e) => handleClickCartLineRemoveButton(e, line.id, line?.variant?.product?.name, true)}
        >
          Move To Wishlist
        </Button>
      </div>
    </div>
  );
};

export default MobileCartLine;
